// src/components/publicDashboard.js (AGGIORNATO CON TRADUZIONI)

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Box, Chip,
    TextField, IconButton, Typography, CircularProgress, Paper, Button, alpha
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CloseIcon from '@mui/icons-material/Close';
import { useDropzone } from 'react-dropzone';
import JSZip from 'jszip';
import api from '../utils/api';
import { getSocket } from '../utils/socketService';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next'; 

const OrderChat = ({ open, onClose, order, currentUser, onChatOpen }) => {
    const { t, i18n } = useTranslation(); // 2. Inizializza il hook
    const [ticket, setTicket] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [attachments, setAttachments] = useState([]);
    const [isZipping, setIsZipping] = useState(false);
    const messagesEndRef = useRef(null);
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (!open || !order?.id) {
            setTicket(null); setMessages([]); setAttachments([]); return;
        }
        const fetchTicketAndRead = async () => {
            setLoading(true);
            try {
                const data = await api.get(`/tickets/order/${order.id}`);
                setTicket(data);
                setMessages(data.messages || []);
                if (onChatOpen) {
                    onChatOpen(data.id);
                }
            } catch (error) {
                console.error("Error fetching ticket:", error);
                Swal.fire(t('publicDashboard.alerts.error'), t('publicDashboard.orderChat.errorLoading'), 'error');
            } finally {
                setLoading(false);
            }
        };
        fetchTicketAndRead();
    }, [open, order?.id, onChatOpen, t]);

    useEffect(() => {
        if (!ticket?.id || !open || !currentUser) return;
        const socket = getSocket();
        socket.emit('join_ticket_room', ticket.id);
        const handleNewMessage = (message) => {
            if (message.ticketId === ticket.id && message.senderId !== currentUser.id) {
                setMessages(prev => [...prev, message]);
            }
        };
        socket.on('new_ticket_message', handleNewMessage);
        return () => {
            socket.emit('leave_ticket_room', ticket.id);
            socket.off('new_ticket_message', handleNewMessage);
        };
    }, [ticket?.id, open, currentUser]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages.length]);
    
    const onDrop = useCallback((acceptedFiles) => {
        setAttachments(prev => {
            const existingNames = new Set(prev.map(f => f.name));
            const newFiles = acceptedFiles.filter(f => !existingNames.has(f.name));
            return [...prev, ...newFiles];
        });
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop, noClick: true, noKeyboard: true,
    });
    
    const removeAttachment = (fileName) => {
        setAttachments(prev => prev.filter(f => f.name !== fileName));
    };

    const handleFileChange = (event) => {
        const files = Array.from(event.target.files);
        if (files.length > 0) onDrop(files);
        if(fileInputRef.current) fileInputRef.current.value = "";
    };

    const handleSendMessage = useCallback(async () => {
        if ((!newMessage.trim() && attachments.length === 0) || !ticket?.id || !currentUser) return;

        const formData = new FormData();
        if (newMessage.trim()) formData.append('content', newMessage);

        const messageContent = newMessage;
        const attachedFiles = [...attachments];
        setNewMessage('');
        setAttachments([]);

        try {
            if (attachedFiles.length > 1) {
                setIsZipping(true);
                const zip = new JSZip();
                attachedFiles.forEach(file => zip.file(file.name, file));
                const zipBlob = await zip.generateAsync({ type: 'blob' });
                const zipFileName = t('publicDashboard.orderChat.attachmentsForOrder', { orderNumber: order.orderNumber });
                const zipFile = new File([zipBlob], zipFileName, { type: 'application/zip' });
                formData.append('attachments', zipFile);
            } else if (attachedFiles.length === 1) {
                formData.append('attachments', attachedFiles[0]);
            }
            
            setIsZipping(false);
            const savedMessage = await api.post(`/tickets/ticket/${ticket.id}/messages`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setMessages(prev => [...prev, savedMessage]);
        } catch (error) {
            console.error("Error sending message:", error);
            setIsZipping(false);
            setNewMessage(messageContent);
            setAttachments(attachedFiles);
            Swal.fire(t('publicDashboard.alerts.error'), t('publicDashboard.orderChat.errorSending'), 'error');
        }
    }, [newMessage, attachments, ticket?.id, currentUser, order?.orderNumber, t]);
    
    const handleDownloadFile = useCallback(async (attachment) => {
        try {
            const token = localStorage.getItem('token');
            if (!token) throw new Error(t('publicDashboard.orderChat.authenticationTokenNotFound'));

            // ✅ CORRETTO - Usa direttamente api.get che gestisce già il baseURL
            const response = await api.get(`/tickets/attachments/${attachment.id}`, {
                responseType: 'blob' // Importante per i file binari
            });

            // Crea il download del blob
            const downloadUrl = window.URL.createObjectURL(response);
            
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = attachment.filename;
            document.body.appendChild(link);
            link.click();
            
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);
            
        } catch (error) {
            console.error("Error during download:", error);
            Swal.fire(
                t('publicDashboard.alerts.error'), 
                t('publicDashboard.orderChat.errorDownloading', { errorMessage: error.message }), 
                'error'
            );
        }
    }, [t]);

    const handleClose = useCallback(() => { setAttachments([]); onClose(); }, [onClose]);
    
    if (!currentUser) return null;

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
            <DialogTitle>
                {t('publicDashboard.orderChat.title', { orderNumber: order?.orderNumber })}
                <IconButton 
                    aria-label="close" 
                    onClick={handleClose} 
                    sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 0, height: '60vh', display: 'flex', flexDirection: 'column', bgcolor: 'grey.50' }}>
                {loading ? <CircularProgress sx={{ m: 'auto' }} /> : (
                    <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2 }}>
                        {(() => {
                            let lastRenderedDate = null;
                            const locale = i18n.language === 'it' ? 'it-IT' : 'en-GB';
                            return messages.map((msg) => {
                                const messageDate = new Date(msg.createdAt);
                                const messageDateString = messageDate.toLocaleDateString(locale, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
                                const isMe = msg.senderId === currentUser.id;
                                const senderName = msg.sender?.companyName || `${msg.sender?.firstName || ''} ${msg.sender?.lastName || ''}`.trim();

                                const dateChip = messageDateString !== lastRenderedDate ? (
                                    <Box key={`date-${messageDateString}`} sx={{ textAlign: 'center', my: 2 }}>
                                        <Chip label={messageDateString} size="small" />
                                    </Box>
                                ) : null;

                                lastRenderedDate = messageDateString;

                                return (
                                    <React.Fragment key={msg.id}>
                                        {dateChip}
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: isMe ? 'flex-end' : 'flex-start', mb: 2 }}>
                                            {msg.sender && senderName && (
                                                <Typography 
                                                    variant="caption" 
                                                    color="text.secondary" 
                                                    sx={{ ml: 1, mr: 1, mb: 0.5, alignSelf: isMe ? 'flex-end' : 'flex-start' }}
                                                >
                                                    {senderName}
                                                </Typography>
                                            )}
                                            <Paper elevation={1} sx={{ p: 1.5, maxWidth: '80%', bgcolor: isMe ? 'primary.light' : 'white' }}>
                                                {msg.content && (
                                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'break-word' }}>
                                                        {msg.content}
                                                    </Typography>
                                                )}
                                                
                                                {msg.attachments && msg.attachments.length > 0 && (
                                                    <Box sx={{ mt: msg.content ? 1.5 : 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                        {msg.attachments.map(att => (
                                                            <Button
                                                                key={att.id}
                                                                onClick={() => handleDownloadFile(att)}
                                                                startIcon={<FileDownloadIcon />}
                                                                variant="outlined" size="small"
                                                                sx={{ textTransform: 'none', justifyContent: 'flex-start' }}
                                                            >
                                                                {att.filename}
                                                            </Button>
                                                        ))}
                                                    </Box>
                                                )}
                                                
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'right', mt: 1 }}>
                                                    {messageDate.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}
                                                </Typography>
                                            </Paper>
                                        </Box>
                                    </React.Fragment>
                                );
                            });
                        })()}
                        <div ref={messagesEndRef} />
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ p: 1, flexDirection: 'column', alignItems: 'stretch' }}>
                <Box {...getRootProps()} sx={{ 
                    border: '2px dashed', 
                    borderColor: isDragActive ? 'primary.main' : 'transparent',
                    backgroundColor: isDragActive ? alpha('#0D47A1', 0.1) : 'transparent',
                    transition: 'all 0.2s ease-in-out',
                    borderRadius: 1,
                    p: attachments.length > 0 || isDragActive ? 1 : 0, 
                    mb: 1
                }}>
                    <input {...getInputProps()} />
                    {isDragActive && <Typography align="center" color="primary.main" sx={{ p: 2 }}>{t('publicDashboard.orderChat.dropFilesHere')}</Typography>}
                    
                    {attachments.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {attachments.map(file => (
                                <Chip
                                    key={file.name}
                                    icon={<AttachFileIcon />}
                                    label={file.name}
                                    onDelete={() => removeAttachment(file.name)}
                                    color="primary"
                                />
                            ))}
                        </Box>
                    )}
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <input type="file" multiple ref={fileInputRef} onChange={handleFileChange} style={{ display: 'none' }} />
                    <IconButton color="primary" onClick={() => fileInputRef.current.click()}>
                        <AttachFileIcon />
                    </IconButton>
                    <TextField
                        fullWidth multiline maxRows={3}
                        autoFocus
                        placeholder={t('publicDashboard.orderChat.writeMessagePlaceholder')}
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyDown={(e) => {
                            if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                                e.preventDefault();
                                handleSendMessage();
                            }
                        }}
                        variant="outlined" size="small"
                    />
                    <IconButton 
                        color="primary" 
                        onClick={handleSendMessage} 
                        disabled={(!newMessage.trim() && attachments.length === 0) || isZipping}
                    >
                        {isZipping ? <CircularProgress size={24} /> : <SendIcon />}
                    </IconButton>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default React.memo(OrderChat);
